import React, { useEffect, useMemo } from 'react'
const { Keyboard, Platform, StyleSheet } = XPReactNative
import {
  Theme,
  generateRegisterWithLabel,
  XStack,
  YStack,
  ActionButton,
  ScrollView,
  ButtonProps,
  KeyBoardAvoidView, XPReactNative, 
  YGroup,
  ListItem,
  Image,
  SimpleText} from '@my/ui'
import type {ProgressStepperProps} from '@my/ui'
import { color, useAuthedPlaidLink, useLink, safeBigNum, MiscUtils } from '@my/util'
import { useForm, FormProvider } from 'react-hook-form'
import { ChevronRight, Info } from '@tamagui/lucide-icons-2'

import { PlaidView } from './helpers/Plaid/plaidView'
import {
  Assets,
  Expenses,
  RiskTolerance,
  FinancialGoals,
  RetirementPlanning,
  DebtsAndLiabilities,
  IncomeAndEmployment,
  LinkedFinancialAccounts,
  ClientIdentificationAndBackground,
  LifeStyle,
  BehavioralAndPsychologicalFactors,
  FamilyAndCulturalConsiderations,
  FinancialSecurity,
  Credit,
  AdditionalInformation,
} from './form-sections/index'
import { PlaidAccountTypeToZAInputMap } from './data'
import { Dictionary, pickBy } from 'lodash'

interface FinancialProfileFormProps {
  data: any
  onSubmit: (data: any, formState: { errors: any, isDirty: boolean, dirtyFields: any }) => void
  onSave: (data: any, formState: { errors: any, isDirty: boolean, dirtyFields: any }) => void
  onDiscard: (data: any) => void
  refreshFinancialInstitution: (institution_obj: any) => Promise<any>
  currentStep: number
  handleStepChange: (data: any) => void
  stepLabels: string[]
  stepsToSkip?: number[]
  reportButtonFns: (buttonFns: ProgressStepperProps['buttonFns']) => void;
  buttonFnsToSkip?: Array<keyof ProgressStepperProps['buttonFns']>;
  isScenarioPlanning?: boolean
}

const stepsFilter = <T extends any>(array: T[], stepsToSkip?: number[]) => array.filter((_, idx) => !(stepsToSkip || []).includes(idx+1));

export const FinancialProfileTableOfContents: React.FC<Pick<FinancialProfileFormProps, 'stepLabels' | 'stepsToSkip' | 'handleStepChange' | 'isScenarioPlanning'>> = (props) => {
  const { stepsToSkip, handleStepChange } = props;
  const stepLabels = stepsFilter(props.stepLabels, stepsToSkip || []);
  return (
    <>
    {props.isScenarioPlanning ? null : <>
      <SimpleText fontSize={20} color={color.tzaGreen} fontWeight={'700'}>
        Let's build your financial profile
      </SimpleText>
      <XStack gap={10} backgroundColor={'rgb(243, 245, 247)'} padding={'$4'} borderRadius={10} borderWidth={1} borderColor={'rgba(220, 224, 229, 0.6)'}>
        <XStack mb={'$2'}>
          <Info width={20} height={20} />
        </XStack>
        <SimpleText fontSize={14} color={'rgb(102, 102, 102)'}>
          The fuller the picture you paint, the more tailored your Parity plan. Parity is a goals-based app, so all of the recommendations will come from the goals you set here & the other information you share. Rough estimates are totally fine to start and you can edit anything after you submit. Nothing here is locked in and it should take about 15 minutes to complete.
        </SimpleText>
      </XStack>
    </>}
    <YGroup bordered size="$5" style={{
      alignSelf: "center",
      minWidth: 240,
      width: "95%",
      gap: 10,
      //maxWidth: 800
    }}>
      {stepLabels.map((stepLabel, idx) => <YGroup.Item key={idx}>
        <ListItem
          hoverTheme
          pressTheme
          title={`${idx+1}. ${stepLabel}`}
          iconAfter={ChevronRight}
          onPress={() => handleStepChange(idx+1)}
          style={{cursor: 'pointer', borderRadius: 9, borderWidth: 1, borderColor: 'rgba(0,0,0,.05)'}}
          {...MiscUtils.ui.shadowGenerator(0.03,5)}
        />
      </YGroup.Item>)}
    </YGroup>
    </>
  )
}

export const FinancialProfileForm: React.FC<FinancialProfileFormProps> = ({
  data,
  onSubmit,
  onSave,
  onDiscard,
  refreshFinancialInstitution,
  currentStep = 1,
  handleStepChange,
  stepsToSkip,
  ...restProps
}) => {
  const stepLabels = stepsFilter(restProps.stepLabels, stepsToSkip || []);
  
  const methods = useForm({
    defaultValues: {
      ...data,
      'Expenses - Categorization Mapping': data['Expenses - Categorization Mapping'] || {},
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    control,
    watch,
    getValues,
    setValue,
    
  } = methods
  const registerWithLabel = generateRegisterWithLabel(register)
  const linkedFinancialDataInput = register('Linked Financial Data');

  const linkedFinancialData = watch(linkedFinancialDataInput.name) || {};
  const handlePlaidSuccess = (plaidData: any) => {
    if (plaidData.institution) {
      //const linkedFinancialData = getValues(linkedFinancialDataInput.name) || {}
      linkedFinancialData[plaidData.institution?.institution_id] = {
        ...plaidData,
        timestamp: Date.now(),
      }
      setValue(linkedFinancialDataInput.name, linkedFinancialData, {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }

  //@ts-ignore
  const { open, ready, error } = useAuthedPlaidLink({
    onSuccess: handlePlaidSuccess,
  })

  const isErrors = Object.keys(errors).length > 0
  const submitErrorProps = isErrors
    ? { disabled: true, disabledStyle: { cursor: 'not-allowed', backgroundColor: color.lightRed } }
    : {}
  const handleFormSubmit = (formData: any) => onSubmit(formData, { errors, isDirty, dirtyFields })
  const handleFormSave = (formData: any) => onSave(formData, { errors, isDirty, dirtyFields })
  const handleFormDiscard = (formData: any) => onDiscard(formData)

  const handleRefreshFinancialInstitution = async (institution_obj: any) => {
    //console.log(institution_obj);
    if (!institution_obj.isManual) {
      const newAccounts = await refreshFinancialInstitution(institution_obj);
      institution_obj.accounts = newAccounts || [];
    }
    
    //const linkedFinancialData = getValues(linkedFinancialDataInput.name) || {};
    if (institution_obj.institution?.institution_id) {
      const newLinkedFinancialData = {
        ...linkedFinancialData,
        [institution_obj.institution?.institution_id]: {
          ...institution_obj,
          timestamp: Date.now(),
        }
      };
      setValue(linkedFinancialDataInput.name, newLinkedFinancialData, {
        shouldDirty: true,
        shouldTouch: true,
      });
      /* const valuesToSet = {} as Dictionary<string[]>;
      Object.values(newLinkedFinancialData).forEach((iObj) => {
        (iObj.accounts as any[] || []).map(acct => {
          const inputKey = PlaidAccountTypeToZAInputMap[acct.type]?.[acct.subtype];
          if (inputKey) {
            if (!valuesToSet[inputKey]) {
              valuesToSet[inputKey] = [];
            }
            valuesToSet[inputKey].push(acct.balances?.available || acct.balances?.current || '');
          }
        })
      });
      Object.entries(valuesToSet).forEach(([inputKey, valueStrArray]) => {
        const sum = valueStrArray.reduce((prev, curr) => prev.plus(safeBigNum(curr)) , safeBigNum(0)).toFixed();
        setValue(inputKey, sum, { shouldDirty: true, shouldTouch: true });
      }); */
      handleSubmit(handleFormSave)();
    }
  }

  const handleDeleteFinancialInstitution = async (institution_obj: any) => {
    if(await MiscUtils.dialog.confirm("Are you sure you would like to delete this financial institution?")) {
      const linkedFinancialData = getValues(linkedFinancialDataInput.name) || {}
      delete linkedFinancialData[institution_obj.institution?.institution_id]
      setValue(linkedFinancialDataInput.name, linkedFinancialData, {
        shouldDirty: true,
        shouldTouch: true,
      })
      handleSubmit(handleFormSave)();
    }
  }

  const goToAddAccountsTab = () => handleStepChange(4)

  const renderStepContent = useMemo(() => {
    const stepComponents = stepsFilter([
      <ClientIdentificationAndBackground control={control} registerWithLabel={registerWithLabel} />,
      <IncomeAndEmployment control={control} />,
      <Expenses control={control} financialProfileData={data} formMethods={methods} />,
      <LinkedFinancialAccounts
        open={open}
        ready={ready}
        linkedFinancialData={linkedFinancialData}
        refreshFinancialInstitution={handleRefreshFinancialInstitution}
        deleteFinancialInstitution={handleDeleteFinancialInstitution}
        control={control}
      />,
      <Assets control={control} linkedFinancialData={linkedFinancialData} goToAddAccountsTab={goToAddAccountsTab} />,
      <DebtsAndLiabilities control={control} linkedFinancialData={linkedFinancialData} goToAddAccountsTab={goToAddAccountsTab} />,
      <Credit control={control} />,
      <FinancialGoals control={control} />,
      <RetirementPlanning control={control} />,
      //<FinancialSecurity control={control} />,
      <FamilyAndCulturalConsiderations control={control} />,
      <BehavioralAndPsychologicalFactors control={control} />,
      <LifeStyle control={control} />,
      <RiskTolerance control={control} />,
      <AdditionalInformation control={control} />,
    ]).filter((_, idx) => !(stepsToSkip || []).includes(idx+1));

    return stepComponents[currentStep - 1] || stepComponents[0];
  }, [
    currentStep,
    control,
    registerWithLabel,
    watch,
    handleRefreshFinancialInstitution,
    handleDeleteFinancialInstitution,
    stepsToSkip
  ])

  const buttonProps: ButtonProps = {
    color: color.white,
    bg: 'rgb(133, 193, 157)',
    width: '48%',
    fontSize: 18,
    fontWeight: '500',
    height: 55,
  } as const

  const submitDisabled = currentStep < stepLabels.length;
  const saveDisabled = !isDirty;
  const discardDisabled = !data?.saved;

  useEffect(() => {
    
    restProps.reportButtonFns(pickBy({
      submit: {
        disabled: submitDisabled,
        fn: handleSubmit(handleFormSubmit)
      },
      save: {
        disabled: saveDisabled,
        fn: handleSubmit(handleFormSave)
      },
      discard: {
        disabled: discardDisabled,
        fn: handleSubmit(handleFormDiscard)
      },
    }, (_, key) => !(restProps.buttonFnsToSkip || []).includes(key)));
  }, [submitDisabled, saveDisabled, discardDisabled]);

  const oldButtons = <YStack width={'100%'} paddingHorizontal={15}>
    {isDirty || data?.saved ? (
      <YStack gap={20}>
        <ActionButton
          hoverStyle={{ opacity: 1 }}
          {...buttonProps}
          onPress={
            currentStep < stepLabels.length
              ? handleSubmit(() => handleStepChange(currentStep + 1))
              : handleSubmit(handleFormSubmit)
          }
          width={'100%'}
          {...submitErrorProps}
        >
          {currentStep < stepLabels.length ? 'Next' : 'Submit'}
        </ActionButton>

        <YStack
          marginVertical={10}
          fd="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <ActionButton
            {...buttonProps}
            onPress={handleSubmit(handleFormSave)}
            disabled={!isDirty}
          >
            Save Changes
          </ActionButton>
          <ActionButton
            {...buttonProps}
            onPress={handleSubmit(handleFormDiscard)}
            disabled={!data?.saved}
            bg={!data?.saved ? 'lightgrey' : '#ff3333'}
          >
            Discard Changes
          </ActionButton>
        </YStack>
      </YStack>
    ) : (
      <XStack width={'100%'} justifyContent={'center'}>
        <ActionButton
          unstyled
          color="white"
          bg="rgb(133, 193, 157)"
          width={'90%'}
          alignItems={'center'}
          alignSelf="center"
          height={55}
          borderRadius={10}
          alignItems="center"
          justifyContent={'center'}
          onPress={
            currentStep < stepLabels.length
              ? handleSubmit(() => handleStepChange(currentStep + 1))
              : handleSubmit(handleFormSubmit)
          }
          {...submitErrorProps}
        >
          {currentStep < stepLabels.length ? 'Next' : 'Submit'}
        </ActionButton>
        <XStack marginVertical={4} />
      </XStack>
    )}
  </YStack>
  const scrollViewContent = <KeyBoardAvoidView props={{style: {flexGrow: 1, marginVertical: 10, width: '100%', gap: 30}}}>{renderStepContent}</KeyBoardAvoidView>
  return (
    <FormProvider {...methods}>
      {false ? <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent}>
        {scrollViewContent}
        {false && oldButtons}
      </ScrollView> : scrollViewContent}
    </FormProvider>
  )
}

const styles = StyleSheet.create({
  scrollView: {
    width: '100%',
    flexGrow: 1
  },
  scrollViewContent: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    gap: 20,
    //paddingBottom: 30,
  },
})
